import { useEffect, useState } from "react";
import { Check, HelpCircle, Loader2, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";

function Question({ q, i, onAnswer }) {
  const [v, setV] = useState(q.saved || "");
  const [busy, setBusy] = useState(false);
  const go = async (e) => { e.preventDefault(); if (!v.trim()) return; setBusy(true); await onAnswer(q.placeholder, v); setBusy(false); };
  return (
    <form onSubmit={go} className="py-2.5 border-b border-slate-100 last:border-0" data-testid={`missing-question-${i}`}>
      <div className="text-sm text-slate-800">{q.question}{q.count > 1 && <span className="text-[11px] text-slate-400 ml-1">({q.count} spots)</span>}</div>
      <div className="flex gap-2 mt-1.5">
        <Input value={v} onChange={(e) => setV(e.target.value)} placeholder="Type the answer" className="h-9 text-sm" data-testid={`missing-answer-input-${i}`} />
        <Button type="submit" size="sm" disabled={busy || !v.trim()} className="bg-emerald-600 hover:bg-emerald-700 h-9" data-testid={`missing-answer-submit-${i}`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}</Button>
      </div>
      {q.saved && <div className="text-[11px] text-emerald-700 mt-1">Filled from a saved answer. Tap the check to use it.</div>}
    </form>
  );
}

export function MissingInfoPanel({ proposalId, content, onContent, disabled }) {
  const [qs, setQs] = useState(null);
  useEffect(() => {
    if (disabled) return;
    const t = setTimeout(() => api.post(`/proposals/${proposalId}/missing`, { content }).then((r) => setQs(r.data)).catch(() => {}), 600);
    return () => clearTimeout(t);
  }, [proposalId, content, disabled]);
  const answer = async (placeholder, value) => {
    try { const { data } = await api.post(`/proposals/${proposalId}/missing/answer`, { placeholder, value, content }); onContent(data.content); toast.success("Draft updated and answer saved"); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const applySaved = async () => {
    try { const { data } = await api.post(`/proposals/${proposalId}/missing/apply-saved`, { content }); onContent(data.content); toast.success(`${data.filled} spot${data.filled === 1 ? "" : "s"} filled from saved answers`); }
    catch (e) { toast.error(formatErr(e)); }
  };
  if (!qs || !content) return null;
  const saved = qs.filter((q) => q.saved).length;
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="missing-info-panel">
      <div className="flex items-center gap-2"><HelpCircle className="h-5 w-5 text-amber-600" /><h3 className="font-display text-lg font-bold text-slate-900">Missing info</h3>
        <span className="ml-auto text-xs font-semibold bg-amber-100 text-amber-900 rounded-full px-2 py-0.5" data-testid="missing-info-count">{qs.length}</span></div>
      {!qs.length ? <p className="text-sm text-emerald-700 mt-3" data-testid="missing-info-done">Every placeholder is filled in.</p> : <>
        <p className="text-xs text-slate-500 mt-1">Answer once. The draft updates and the answer is reused in future proposals.</p>
        {saved > 0 && <Button variant="outline" size="sm" className="w-full mt-3" onClick={applySaved} disabled={disabled} data-testid="missing-apply-saved-button"><Wand2 className="h-4 w-4 mr-2" />Fill {saved} from saved answers</Button>}
        <div className="mt-3 max-h-[380px] overflow-y-auto pr-1">{qs.map((q, i) => <Question key={q.placeholder} q={q} i={i} onAnswer={answer} />)}</div>
      </>}
    </div>
  );
}
